import {
  HHChip,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  buildIngestionDetailPath,
  FILE_PROCESSING_STATUS_LABELS,
  PROVIDER_HEALTH_LABELS,
} from '../constants/partner-data-gateway.constant';
import { useBasePath } from '../context/base-path-context';
import { ProviderSummary } from '../domain/types';
import { getProviderHealth } from '../utils/provider-health.utils';
import { RelativeTimeCell } from './relative-time-cell';
import { StatusChip } from './status-chip';

export const PROVIDER_HEADER_TEST_ID = 'pdg-provider-header';
export const PROVIDER_HEADER_HEALTH_TEST_ID = 'pdg-provider-header-health';
export const PROVIDER_HEADER_NO_CONFIGURATION_TEST_ID =
  'pdg-provider-header-no-configuration';
export const PROVIDER_HEADER_LATEST_LINK_TEST_ID =
  'pdg-provider-header-latest-link';
export const PROVIDER_HEADER_LATEST_STATUS_TEST_ID =
  'pdg-provider-header-latest-status';

export const NO_CONFIGURATION_CHIP_LABEL = 'No configuration';
export const PROVIDER_HEADER_LATEST_LABEL = 'Latest file';
export const PROVIDER_HEADER_NO_FILES_TEXT = 'No files yet.';

interface ProviderHeaderProps {
  summary: ProviderSummary;
}

/**
 * Hero for the provider detail page: the bucket name is the page's `h1`
 * (20/600 per the v4 type scale), health beside it, and the latest file as
 * the shortest path into the pipeline that last ran.
 */
export const ProviderHeader = ({
  summary,
}: ProviderHeaderProps): JSX.Element => {
  const basePath = useBasePath();
  const health = getProviderHealth(summary);
  const latest = summary.latestIngestion;

  return (
    <HHStack gap={2} data-testid={PROVIDER_HEADER_TEST_ID}>
      <HHStack direction="row" alignItems="center" gap={2} flexWrap="wrap">
        <HHTypography
          hhVariant="variant-bypass"
          variant="h5"
          component="h1"
          sx={{
            fontSize: 20,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            wordBreak: 'break-all',
          }}
        >
          {summary.dataProvider}
        </HHTypography>
        <StatusChip
          status={health}
          label={PROVIDER_HEALTH_LABELS[health]}
          testId={PROVIDER_HEADER_HEALTH_TEST_ID}
        />
        {!summary.hasConfiguration && (
          <HHChip
            hhVariant="variant-bypass"
            variant="outlined"
            size="small"
            label={NO_CONFIGURATION_CHIP_LABEL}
            data-testid={PROVIDER_HEADER_NO_CONFIGURATION_TEST_ID}
          />
        )}
      </HHStack>
      {latest ? (
        <HHStack direction="row" alignItems="center" gap={2} flexWrap="wrap">
          <HHTypography
            hhVariant="variant-bypass"
            variant="body2"
            color="text.secondary"
          >
            {PROVIDER_HEADER_LATEST_LABEL}
          </HHTypography>
          <Link
            component={RouterLink}
            to={`${basePath}/${buildIngestionDetailPath(latest.eventId)}`}
            underline="hover"
            color="text.primary"
            variant="body2"
            sx={{ fontWeight: 500, wordBreak: 'break-all' }}
            data-testid={PROVIDER_HEADER_LATEST_LINK_TEST_ID}
          >
            {latest.fileName}
          </Link>
          <StatusChip
            status={latest.status}
            label={FILE_PROCESSING_STATUS_LABELS[latest.status]}
            testId={PROVIDER_HEADER_LATEST_STATUS_TEST_ID}
          />
          <RelativeTimeCell isoDate={latest.createdAt} />
        </HHStack>
      ) : (
        <HHTypography
          hhVariant="variant-bypass"
          variant="body2"
          color="text.secondary"
        >
          {PROVIDER_HEADER_NO_FILES_TEXT}
        </HHTypography>
      )}
    </HHStack>
  );
};
